import { Eye, FileSpreadsheet, FileText, FileDown, Loader2 } from "lucide-react";
import { useState, useEffect } from "react";
import { Button } from "@/components/ui/button";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { toast } from "sonner";
import { TXN, inr, num } from "@/lib/format";
import { api, errMsg } from "@/lib/api";

export function PageHeader({ title, subtitle, actions }) {
  return (
    <div className="flex flex-col sm:flex-row sm:items-end sm:justify-between gap-4 mb-6 fade-up">
      <div>
        <h1 className="text-2xl sm:text-3xl font-bold tracking-tight text-slate-900" data-testid="page-title">{title}</h1>
        {subtitle && <p className="text-sm text-slate-500 mt-1">{subtitle}</p>}
      </div>
      {actions && <div className="flex flex-wrap gap-2 no-print">{actions}</div>}
    </div>
  );
}

export function StatCard({ label, value, icon: Icon, tone = "slate", hint, testid }) {
  const tones = {
    slate: "text-slate-900",
    red: "text-red-700",
    green: "text-emerald-700",
    blue: "text-blue-800",
  };
  return (
    <div className="bg-white rounded-xl border border-slate-200 p-5 shadow-sm hover:shadow-md transition-shadow duration-200" data-testid={testid}>
      <div className="flex items-start justify-between gap-2">
        <div className="text-[11px] font-semibold uppercase tracking-wider text-slate-500">{label}</div>
        {Icon && <Icon className="h-4 w-4 text-slate-400" />}
      </div>
      <div className={`mt-3 money text-xl sm:text-2xl font-bold ${tones[tone]}`} data-testid={testid && `${testid}-value`}>{value}</div>
      {hint && <div className="text-xs text-slate-500 mt-1">{hint}</div>}
    </div>
  );
}

export function TxnBadge({ type, reversed }) {
  const t = TXN[type] || { label: type, cls: "" };
  return (
    <span className="inline-flex items-center gap-1.5">
      <span className={`inline-flex rounded-md border px-2 py-0.5 text-xs font-medium whitespace-nowrap ${t.cls}`}>{t.label}</span>
      {reversed && <span className="text-[10px] uppercase font-semibold text-slate-500 border border-slate-300 rounded px-1">Reversed</span>}
    </span>
  );
}

export function Money({ value, blankZero, className = "" }) {
  if (blankZero && !Number(value)) return <span className="text-slate-300">—</span>;
  return <span className={`money ${className}`}>{inr(value)}</span>;
}

export function ReadOnlyBanner({ text = "You have view-only access. Recording or editing transactions is disabled." }) {
  return (
    <div className="mb-5 flex items-center gap-2 rounded-lg border border-amber-300 bg-amber-50 px-4 py-3 text-sm text-amber-800" data-testid="readonly-banner">
      <Eye className="h-4 w-4 shrink-0" /> {text}
    </div>
  );
}

export function ExportButtons({ onExport, prefix, extra }) {
  const [busy, setBusy] = useState("");
  const run = async (fmt) => {
    setBusy(fmt);
    try {
      await onExport(fmt);
    } catch (e) {
      toast.error("Export failed. Please try again.");
    } finally {
      setBusy("");
    }
  };
  const items = [
    ["xlsx", "Excel", FileSpreadsheet],
    ["csv", "CSV", FileDown],
    ["pdf", "PDF", FileText],
  ];
  return (
    <div className="flex flex-wrap gap-2 no-print">
      {items.map(([f, l, I]) => (
        <Button key={f} variant="outline" size="sm" onClick={() => run(f)} disabled={!!busy} data-testid={`${prefix}-export-${f}`}>
          {busy === f ? <Loader2 className="h-4 w-4 animate-spin" /> : <I className="h-4 w-4" />} {l}
        </Button>
      ))}
      {extra}
    </div>
  );
}

const ALL = "__all";
export function FilterSelect({ value, onChange, options, placeholder, testid, className = "w-full sm:w-44" }) {
  return (
    <Select value={value || ALL} onValueChange={(v) => onChange(v === ALL ? "" : v)}>
      <SelectTrigger className={`${className} bg-white`} data-testid={testid}>
        <SelectValue placeholder={placeholder} />
      </SelectTrigger>
      <SelectContent>
        <SelectItem value={ALL}>{placeholder}</SelectItem>
        {options.map((o) => (
          <SelectItem key={o.value ?? o} value={o.value ?? o}>{o.label ?? o}</SelectItem>
        ))}
      </SelectContent>
    </Select>
  );
}

export function useMeta() {
  const [meta, setMeta] = useState({ departments: [], branches: [] });
  useEffect(() => {
    api.get("/employees/meta").then((r) => setMeta(r.data)).catch((e) => toast.error(errMsg(e)));
  }, []);
  return meta;
}

export function ReportTable({ data, testid }) {
  if (!data) return null;
  const money = new Set(data.money_cols);
  const cellFmt = (c, i) => (money.has(i) && typeof c === "number" ? num(c) : c === "" || c == null ? "—" : c);
  return (
    <div className="bg-white rounded-xl border border-slate-200 overflow-x-auto" data-testid={testid}>
      <table className="w-full text-sm">
        <thead className="bg-slate-50 border-b border-slate-200">
          <tr>
            {data.headers.map((h, i) => (
              <th key={h} className={`px-3 py-2.5 text-[11px] font-semibold uppercase tracking-wider text-slate-500 whitespace-nowrap ${money.has(i) ? "text-right" : "text-left"}`}>
                {h}{money.has(i) ? " (₹)" : ""}
              </th>
            ))}
          </tr>
        </thead>
        <tbody>
          {data.rows.length === 0 && (
            <tr><td colSpan={data.headers.length} className="text-center text-slate-500 py-10">No records for the selected filters.</td></tr>
          )}
          {data.rows.map((r, ri) => (
            <tr key={ri} className="border-b border-slate-100 hover:bg-slate-50 transition-colors">
              {r.map((c, i) => (
                <td key={i} className={`px-3 py-2 whitespace-nowrap ${money.has(i) ? "text-right money" : ""}`}>{cellFmt(c, i)}</td>
              ))}
            </tr>
          ))}
        </tbody>
        {data.totals && data.rows.length > 0 && (
          <tfoot className="bg-slate-100 font-semibold" data-testid={`${testid}-totals`}>
            <tr>
              {data.totals.map((c, i) => (
                <td key={i} className={`px-3 py-2.5 whitespace-nowrap ${money.has(i) ? "text-right money" : ""}`}>{c === "" ? "" : cellFmt(c, i)}</td>
              ))}
            </tr>
          </tfoot>
        )}
      </table>
    </div>
  );
}
